import type { ReactNode } from 'react';
import { Icon, Tone } from '~/components/ui';
import { useT } from '~/i18n';
import type { AppliedTodayJob, JobForYou, LockedJobPreview, ManualQuota } from '../api/freeTierClient';
import { formatJobWage } from '../lib/demo';
import { formatStartDate, visaLabel } from '../lib/jobsForYou';

/**
 * «Vacantes para ti»: las del feed, elegidas por la hoja de vida con el mismo
 * filtro del envío del plan.
 *
 * Desde el 2026-10-30 (decisión de Clovis) la recomendación es del plan:
 * - Sin plan va borrosa. El servidor manda solo visa, salario e inicio, así que
 *   no hay nada que leer debajo del borroso. Las mismas vacantes están en el
 *   buscador (`JobSearchCard`), gratis: el plan paga el trabajo de elegirlas y
 *   postular, no el acceso (términos §8).
 * - Con plan, arriba va lo que el envío mandó hoy («Hoy postulamos por ti») y
 *   debajo las recomendadas, que también se pueden postular a mano.
 *
 * Las cerradas (el empleador ya dijo «no hay cupo», ADR-046) van sin botón.
 */
interface Props {
  /** null mientras carga. */
  jobs: JobForYou[] | null;
  quota: ManualQuota | null;
  failed: boolean;
  hasGmail: boolean;
  /** Postuladas en esta sesión (el servidor ya no las devuelve al recargar). */
  applied: ReadonlySet<string>;
  onApply: (job: JobForYou) => void;
  locked?: boolean;
  preview?: LockedJobPreview[];
  appliedToday?: AppliedTodayJob[];
  /** Sin plan: abre la oferta del plan. */
  onUnlock?: () => void;
  /** Sin plan: lleva al buscador, para quien prefiere buscar por su cuenta. */
  onSearchSelf?: () => void;
  /** Las guardadas, para pintar el botón de guardar prendido o apagado. */
  saved?: ReadonlySet<string>;
  onToggleSave?: (job: JobForYou) => void;
}

function wageOf(job: Pick<JobForYou, 'wage_from' | 'wage_to' | 'wage_per' | 'wage_currency'>): string | null {
  return formatJobWage({
    title: '',
    company: '',
    wage_from: job.wage_from,
    wage_to: job.wage_to,
    wage_per: job.wage_per,
    wage_currency: job.wage_currency,
  });
}

/** El botón de guardar de una fila: prendido si ya está en «Guardadas». */
export interface SaveToggle {
  saved: boolean;
  onToggle: () => void;
}

/** Arma el `SaveToggle` de una vacante, o nada si la superficie no guarda. */
export function saveToggleFor(
  job: JobForYou,
  saved: ReadonlySet<string> | undefined,
  onToggleSave: ((job: JobForYou) => void) | undefined,
): SaveToggle | undefined {
  if (!onToggleSave) return undefined;
  return { saved: saved?.has(job._id) ?? false, onToggle: () => onToggleSave(job) };
}

function SaveButton({ saved, onToggle }: SaveToggle) {
  const t = useT('workspace').dashboard.jobsForYou.saved;
  const label = saved ? t.unsave : t.save;
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm btn-square"
      aria-label={label}
      aria-pressed={saved}
      title={label}
      onClick={onToggle}
      style={saved ? { color: 'var(--purple-ink)' } : { color: 'var(--ink-faint)' }}
    >
      <Icon name="bookmark" filled={saved} />
    </button>
  );
}

/** Una vacante con su botón (o su chip). La comparten la recomendación, el buscador y las guardadas. */
export function JobRow({ job, action, save }: { job: JobForYou; action: ReactNode; save?: SaveToggle }) {
  const t = useT('workspace').dashboard.jobsForYou;
  const wage = wageOf(job);
  const start = formatStartDate(job.start_date, 'short');
  const where = [job.location, visaLabel(job.visa_type, t.jobBank), start && t.starts(start)].filter(Boolean).join(' · ');
  return (
    <div className="p-3 rounded-2xl" style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[12rem]">
          <p className="font-medium">
            {job.company} · {job.title}
          </p>
          {where && <p className="text-xs hush">{where}</p>}
          {wage && (
            /* Mint: un dato real de la oferta, no una promesa nuestra. */
            <p className="text-xs text-data font-medium">{wage}</p>
          )}
        </div>
        {(action || save) && (
          <div className="flex items-center gap-2 shrink-0">
            {action}
            {save && <SaveButton {...save} />}
          </div>
        )}
      </div>
    </div>
  );
}

/** El botón de postular a mano, o el chip que lo reemplaza. */
export function ApplyAction({
  job,
  isApplied,
  appliedLabel,
  hasGmail,
  onApply,
}: {
  job: JobForYou;
  isApplied: boolean;
  appliedLabel: string;
  hasGmail: boolean;
  onApply: (job: JobForYou) => void;
}) {
  const t = useT('workspace').dashboard.jobsForYou;
  if (isApplied) return <Tone tone="good">{appliedLabel}</Tone>;
  if (job.closed) return <Tone tone="muted">{t.closed}</Tone>;
  if (!job.contact_email_present) return null;
  return (
    <button type="button" className="btn btn-primary btn-sm gap-2" onClick={() => onApply(job)}>
      <Icon name={hasGmail ? 'send' : 'mail'} />
      {hasGmail ? t.applyGmail : t.applyCopy}
    </button>
  );
}

/** Anchos de las siluetas, para que las filas no salgan calcadas. */
const GHOST_WIDTHS: Array<[string, string]> = [
  ['58%', '34%'],
  ['46%', '28%'],
  ['64%', '38%'],
  ['52%', '24%'],
  ['42%', '31%'],
];

/** Una silueta de texto: una pastilla lila tenue, apenas desenfocada. */
function Ghost({ width, height }: { width: string; height: string }) {
  return (
    <span
      className="block rounded-full"
      style={{
        width,
        height,
        background: 'linear-gradient(90deg, var(--lila), color-mix(in srgb, var(--lila) 35%, transparent))',
        opacity: 0.55,
        filter: 'blur(1.5px)',
      }}
    />
  );
}

/**
 * Una fila bloqueada: lo que no identifica la vacante se ve; empresa, cargo y
 * lugar son siluetas suaves (el servidor no los manda, no hay nada debajo).
 */
function LockedRow({ item, index }: { item: LockedJobPreview; index: number }) {
  const t = useT('workspace').dashboard.jobsForYou;
  const wage = wageOf(item);
  const start = formatStartDate(item.start_date, 'short');
  const meta = [visaLabel(item.visa_type, t.jobBank), start && t.starts(start)].filter(Boolean).join(' · ');
  const [w1, w2] = GHOST_WIDTHS[index % GHOST_WIDTHS.length];
  return (
    <div
      className="p-3 rounded-2xl flex items-center gap-4"
      style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
      aria-label={t.lockedRow}
    >
      <div className="flex-1 min-w-0">
        <div aria-hidden="true" className="space-y-2 py-1" style={{ userSelect: 'none' }}>
          <Ghost width={w1} height="0.7rem" />
          <Ghost width={w2} height="0.5rem" />
        </div>
        {(meta || wage) && (
          <p className="text-xs mt-1.5">
            {meta && <span className="hush">{meta}</span>}
            {meta && wage && <span className="hush"> · </span>}
            {wage && <span className="text-data font-medium">{wage}</span>}
          </p>
        )}
      </div>
      <span aria-hidden="true" style={{ color: 'var(--ink-faint)' }}>
        <Icon name="lock" />
      </span>
    </div>
  );
}

export default function JobsForYouCard({
  jobs,
  quota,
  failed,
  hasGmail,
  applied,
  onApply,
  locked = false,
  preview = [],
  appliedToday = [],
  onUnlock,
  onSearchSelf,
  saved,
  onToggleSave,
}: Props) {
  const t = useT('workspace').dashboard.jobsForYou;

  if (locked) {
    return (
      <div className="card rise">
        <div className="card-body space-y-3">
          <h2 className="card-title">{t.title}</h2>
          {preview.length === 0 ? (
            <p className="text-sm hush">{t.empty}</p>
          ) : (
            <>
              <p className="text-sm">{t.lockedLede(preview.length)}</p>
              <div className="relative space-y-3">
                {preview.slice(0, 5).map((item, i) => (
                  <LockedRow key={i} item={item} index={i} />
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                {onUnlock && (
                  <button type="button" className="btn btn-primary gap-2" onClick={onUnlock}>
                    <Icon name="sparkle" />
                    {t.lockedCta}
                  </button>
                )}
                {onSearchSelf && (
                  <button type="button" className="btn btn-ghost gap-2" onClick={onSearchSelf}>
                    <Icon name="search" />
                    {t.lockedSearchSelf}
                  </button>
                )}
              </div>
              <p className="text-xs hush">{t.lockedSearchHint}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card rise">
      <div className="card-body space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="card-title">{t.title}</h2>
          {quota && <span className="text-xs hush">{t.quota(quota.used, quota.limit)}</span>}
        </div>

        {appliedToday.length > 0 && (
          <section className="space-y-3" aria-label={t.appliedTodayTitle(appliedToday.length)}>
            <h3 className="font-semibold text-sm">{t.appliedTodayTitle(appliedToday.length)}</h3>
            {appliedToday.map((job) => (
              <JobRow key={job._id} job={job} action={<Tone tone="good">{t.appliedTodayChip}</Tone>} />
            ))}
            <h3 className="font-semibold text-sm pt-2">{t.recommendedTitle}</h3>
          </section>
        )}

        {failed ? (
          <p className="text-sm" style={{ color: 'var(--coral-ink)' }}>
            {t.failed}
          </p>
        ) : !jobs ? (
          <p className="text-sm hush flex items-center gap-2">
            <span className="loading loading-spinner loading-xs" />
            {t.loading}
          </p>
        ) : jobs.length === 0 ? (
          <p className="text-sm hush">{t.empty}</p>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <JobRow
                key={job._id}
                job={job}
                save={saveToggleFor(job, saved, onToggleSave)}
                action={
                  <ApplyAction
                    job={job}
                    isApplied={applied.has(job._id)}
                    appliedLabel={t.applied}
                    hasGmail={hasGmail}
                    onApply={onApply}
                  />
                }
              />
            ))}
          </div>
        )}

        <p className="text-xs hush">{t.footer}</p>
      </div>
    </div>
  );
}
